import { DateTime } from "luxon";

import type { SafetyProfileStat } from "@miter/types/contracts";
import { roundTo } from "miter-utils/round-to";

const HEADLINE_RATE_DECIMALS = 2;

const PERCENT: Intl.NumberFormatOptions = { style: "percent", maximumFractionDigits: 0 };
const RATE: Intl.NumberFormatOptions = { maximumFractionDigits: 1 };
const TWO_DECIMALS: Intl.NumberFormatOptions = {
  minimumFractionDigits: HEADLINE_RATE_DECIMALS,
  maximumFractionDigits: HEADLINE_RATE_DECIMALS,
};

export const FULL_DATE = "DDD";

export const formatNumber = (value: number, options?: Intl.NumberFormatOptions): string =>
  new Intl.NumberFormat("en-US", options).format(value);

export const formatDate = (iso: string | null | undefined, format = FULL_DATE): string =>
  iso ? DateTime.fromISO(iso).toFormat(format) : "";

export const formatRate = (value: number): string => formatNumber(value, TWO_DECIMALS);

// Compare at the printed precision so a rate that rounds onto its benchmark reads as even.
export const headlineDelta = (value: number, benchmark: number): number =>
  roundTo(benchmark, HEADLINE_RATE_DECIMALS) - roundTo(value, HEADLINE_RATE_DECIMALS);

export type FormattedStat = { value: string; caption: string | null };

export const formatStat = (stat: SafetyProfileStat): FormattedStat => {
  const { value } = stat;
  if (!value) return { value: "Not reported", caption: null };

  switch (value.type) {
    case "count":
      return { value: formatNumber(value.count), caption: null };
    case "share":
      return { value: `${formatNumber(value.covered)} of ${formatNumber(value.total)} jobs`, caption: null };
    case "rate":
      return {
        value: `${formatNumber(value.value, RATE)} per ${formatNumber(value.per_hours)} hours`,
        caption: null,
      };
    case "closure":
      return {
        value: `${formatNumber(value.median_days)} days median, ${formatNumber(value.on_time_share, PERCENT)} on time`,
        caption: null,
      };
    case "streak":
      return { value: formatNumber(value.days), caption: null };
  }
};
